import Link from "next/link";
import type { ParagraphStoryCard as ParagraphStoryCardData } from "@/lib/api/cms-catalog";
import type { ParagraphTranslations } from "../Paragraph.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./ParagraphStoryCard.module.css";

/**
 * Layout 58 - `PARAGRAPH_STORY_CARD_LAYOUT`: karta z opisem HTML + zdjecie (2 kol).
 *
 * Struktura:
 *   - Header sekcji: eyebrow (text_1) + H2 (name) + intro (text)
 *   - Karta 2-kol:
 *       - Lewa: badge + card.title + card.subtitle + body (HTML) + opcjonalny quote + opcjonalny CTA
 *       - Prawa: full-height image (image_1 z imp_paragraph_image, sort=1)
 *
 * Uniwersalny pattern - case study spotlight, product highlight, story feature.
 */
export function ParagraphStoryCard({
  paragraph,
}: {
  paragraph: ParagraphStoryCardData;
  t: ParagraphTranslations;
}) {
  const { text_1: eyebrow, name: title, text: intro, card, image } = paragraph;
  const hasEyebrow = eyebrow && eyebrow.trim() !== "";
  const hasIntro = intro && intro.trim() !== "";
  const hasBody = card.body && card.body.trim() !== "";

  return (
    <section className={styles.section}>
      <div className={styles.header}>
        {hasEyebrow ? <p className={styles.eyebrow}>{eyebrow}</p> : null}
        {title ? <h2 className={styles.title}>{title}</h2> : null}
        {hasIntro ? <p className={styles.intro}>{intro}</p> : null}
      </div>

      <div className={styles.card} data-no-image={image ? "false" : "true"}>
        <div className={styles.cardLeft}>
          {card.badge ? <span className={styles.badge}>{card.badge}</span> : null}
          {card.title ? <h3 className={styles.cardTitle}>{card.title}</h3> : null}
          {card.subtitle ? <p className={styles.cardSubtitle}>{card.subtitle}</p> : null}
          {hasBody ? (
            <div
              className={styles.body}
              dangerouslySetInnerHTML={{ __html: card.body }}
            />
          ) : null}

          {card.stats.length > 0 ? (
            <dl className={styles.stats}>
              {card.stats.map((s, i) => (
                <div key={i} className={styles.statItem}>
                  {s.value ? <dt className={styles.statValue}>{s.value}</dt> : null}
                  {s.label ? <dd className={styles.statLabel}>{s.label}</dd> : null}
                </div>
              ))}
            </dl>
          ) : null}

          {card.quote ? (
            <blockquote className={styles.quote}>
              <p className={styles.quoteText}>„{card.quote.text}"</p>
              {(card.quote.author || card.quote.author_role) ? (
                <footer className={styles.quoteFooter}>
                  {card.quote.author ? <strong>{card.quote.author}</strong> : null}
                  {card.quote.author && card.quote.author_role ? ", " : null}
                  {card.quote.author_role ? <span>{card.quote.author_role}</span> : null}
                </footer>
              ) : null}
            </blockquote>
          ) : null}

          {card.cta ? <CtaButton label={card.cta.label} url={card.cta.url} /> : null}
        </div>

        {image ? (
          <div className={styles.cardRight}>
            <img
              src={image.full?.url ?? image.url}
              alt={image.alt ?? card.title ?? ""}
              className={styles.image}
              loading="lazy"
            />
          </div>
        ) : null}
      </div>
    </section>
  );
}

function CtaButton({ label, url }: { label: string; url: string }) {
  const href = extractInternalHref(url);
  return href !== null ? (
    <Link href={href} className={styles.cta}>
      {label}
    </Link>
  ) : (
    <a href={url} className={styles.cta}>
      {label}
    </a>
  );
}
